import { Link } from 'react-router-dom';
import PageShell, { PageTitle } from '../components/PageShell';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import { ABOUT_PARAGRAPHS, DISCLAIMER_TEXT } from '../constants/content';
import {
  ADDRESS,
  BRAND_NAME,
  COMPANY_NAME,
  SITE_DOMAIN,
  SUPPORT_PHONE_LABEL,
  SUPPORT_PHONE_TEL,
} from '../constants/site';
import { IMAGES } from '../constants/images';
import { openZohoChat } from '../utils/openZohoChat';

const SERVICES = [
  ['Install & Activate', 'Getting Family Tree Maker onto Windows 10, Windows 11, or macOS and past any serial number or activation hurdles.'],
  ['Upgrade Safely', 'Carrying trees from FTM 2014, 2017, or 2019 into FTM 2024 without losing a single name, source, or picture.'],
  ['Sort Out Syncing', 'Clearing FamilySync errors, frozen progress bars, duplicate people, and broken Ancestry links.'],
  ['Protect Your Work', 'Setting up TreeVault and reliable backups, and bringing trees back after a mishap.'],
  ['Find Lost Media', 'Reconnecting photos and documents that went missing after a file move or computer change.'],
  ['Stop the Crashes', 'Diagnosing trees that will not open, programs that freeze, and puzzling error messages.'],
  ['Change Computers', 'Moving your tree, media, and settings to a new PC or Mac, including Windows-to-Mac switches.'],
  ['Learn the Tools', 'Charts, reports, color coding, relationships, citations, and the Family Book Creator plugin.'],
];

const VALUES = [
  ['Backup before anything', 'We never touch a tree until a safe copy exists. Your research is irreplaceable, and we treat it that way.'],
  ['Explain, don’t lecture', 'You will hear what went wrong in everyday language, plus one or two tips to avoid it next time.'],
  ['No edition left behind', 'Brand new or well over a decade old, on Windows or Mac, we will help with the copy you have.'],
  ['Here when you need us', 'Live chat stays open around the clock, and a real person answers the phone.'],
];

const PROCESS = [
  ['You tell us', 'Call or chat and describe what is happening, including your edition and computer.'],
  ['We investigate', 'A technician asks a few targeted questions to narrow down the cause.'],
  ['We fix it together', 'Step-by-step guidance, or a remote session once you say yes.'],
  ['We leave you safer', 'Before we finish, your backups are working and you know how to use them.'],
];

const WHO_WE_HELP = [
  ['Complete beginners', 'who have just bought Family Tree Maker and want help entering their first generations.'],
  ['Returning researchers', 'dusting off a tree they built years ago on an older edition or an old computer.'],
  ['Serious genealogists', 'managing very large trees who need performance, syncing, or data-integrity help.'],
  ['Family historians on a mission', 'preparing a book, reunion chart, or gift and wanting it to print perfectly.'],
];

export default function AboutPage() {
  return (
    <PageShell>
      <PageTitle title="About Us" crumbs={[{ label: 'About Us' }]} />

      <div className="media-row">
        <img
          src={IMAGES.portraits}
          alt="Old family portraits laid out for genealogy research"
          width={700}
          height={1050}
          loading="lazy"
        />
        <div>
          <h2>Hello from {BRAND_NAME}</h2>
          {ABOUT_PARAGRAPHS.map((text) => (
            <p key={text}>{text}</p>
          ))}
        </div>
      </div>

      <CallButton />

      <section className="entry-section">
        <h2>Why We Do This</h2>
        <p>
          It started with phone calls from relatives. One had upgraded Windows and could no longer open
          a tree built over twenty years. Another watched a sync run all night and woke up to hundreds of
          duplicate cousins. In almost every case the research was perfectly safe; it just needed
          someone calm to find it and put things right.
        </p>
        <p>
          Those calls turned into {BRAND_NAME}. Today we help people all over the country, from
          first-time users to researchers with trees of fifty thousand names. A lot of our callers are
          retired and exploring their roots for the first time, and we make a point of never rushing
          anyone.
        </p>
      </section>

      <section className="entry-section">
        <h2>How We Can Help</h2>
        <div className="service-grid">
          {SERVICES.map(([title, text]) => (
            <div key={title} className="service">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Who We Help</h2>
        <ul>
          {WHO_WE_HELP.map(([title, text]) => (
            <li key={title}>
              <strong>{title}</strong> {text}
            </li>
          ))}
        </ul>
      </section>

      <section className="entry-section">
        <h2>Our Four-Step Approach</h2>
        <div className="steps-grid">
          {PROCESS.map(([title, text]) => (
            <div key={title} className="service">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="entry-section">
        <h2>The Principles We Work By</h2>
        <div className="service-grid">
          {VALUES.map(([title, text]) => (
            <div key={title} className="service">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>What Sets Us Apart</h2>
        <ul>
          <li><strong>Always-open live chat</strong> — including evenings, weekends, and holidays.</li>
          <li><strong>Fluent in Windows and Mac</strong> — and in moving trees between the two.</li>
          <li><strong>Support for older editions</strong> — even releases many others no longer touch.</li>
          <li><strong>Honest pricing</strong> — you agree to any cost first, and our <Link to="/refund-policy">Refund Policy</Link> protects you.</li>
          <li><strong>Your privacy respected</strong> — remote access only with permission; read our <Link to="/privacy-policy">Privacy Policy</Link>.</li>
          <li><strong>A free help library</strong> — our <Link to="/blogs">guides</Link> answer the most common questions step by step.</li>
        </ul>
      </section>

      <HelpBox
        title="Say Hello to Our Team"
        text="No appointment, no ticket number. Call or open live chat and tell us what is going on."
      />

      <section className="entry-section">
        <h2>Business Information</h2>
        <p>
          {BRAND_NAME} ({SITE_DOMAIN}) is operated by <strong>{COMPANY_NAME}</strong>, {ADDRESS}. You can
          reach us by phone at <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a>, through{' '}
          <button type="button" className="text-link" onClick={openZohoChat}>
            live chat
          </button>
          , or using the form on our <Link to="/contact-us">Contact Us page</Link>.
        </p>
      </section>

      <section className="entry-section">
        <h2>Independence Statement</h2>
        <p>{DISCLAIMER_TEXT}</p>
      </section>

      <CallButton />
    </PageShell>
  );
}
